/**
 * How far along a target is.
 *
 * Derived from the base, the target and where things stand, rather than typed,
 * so the bar and the numbers beside it cannot disagree. A progress figure that
 * has to be kept in step by hand is one that will eventually say 80% next to a
 * figure that is 30% of the way there, and nobody reading it would know which
 * to believe.
 *
 * **The direction is not a setting.** A target to cut emissions and a target to
 * raise the share of renewables are the same calculation: the distance
 * travelled from the base towards the target, over the distance to be
 * travelled. Making the author declare which way it runs would be one more
 * thing to get wrong, and getting it wrong would turn a failing target into a
 * passing one.
 *
 * Values arrive as strings because that is how they were published, with their
 * own separators and their own precision. Parsing is forgiving about the
 * separators people actually use and refuses everything else: a progress bar
 * drawn from a misread number is worse than no bar at all.
 *
 * Pure. Numbers in, numbers out.
 */

/** What can be said about a target's progress. */
export interface Progress {
  /** Between 0 and 1. Clamped: a target overshot still reads as done. */
  readonly fraction: number
  /** Whether it has been reached or passed. */
  readonly reached: boolean
  /**
   * True when the current figure is the wrong side of the base.
   *
   * Worth saying rather than showing as zero. A target moving away from its
   * base is not a target at 0%: one has not started, the other is going
   * backwards, and a bar cannot tell them apart.
   */
  readonly regressing: boolean
}

/**
 * A published figure read as a number, or null.
 *
 * Accepts the separators a figure actually arrives with: a space or a
 * non-breaking space for thousands, and either a point or a comma for the
 * decimal. Accepts a percentage sign and accounting parentheses, because both
 * turn up in a target. Refuses everything else rather than guessing: this
 * feeds a bar somebody reads as fact.
 */
export function readFigure(text: string): number | null {
  const trimmed = text.trim()
  if (trimmed === '') return null

  const negative = /^\(.*\)$/.test(trimmed)

  const bare = trimmed
    .replace(/^\(|\)$/g, '')
    .replace(/[\s  ]/g, '')
    .replace(/%$/, '')
    // A comma is the decimal separator wherever this is used, and a point is
    // the thousands separator, so a point before three digits is dropped and
    // a comma becomes the point.
    .replace(/\.(?=\d{3}\b)/g, '')
    .replace(',', '.')

  if (bare === '' || bare === '-' || bare === '+') return null

  const value = Number(bare)
  if (!Number.isFinite(value)) return null

  return negative ? -Math.abs(value) : value
}

/**
 * How far a target has come, or null when it cannot be said.
 *
 * Null rather than zero wherever a figure is missing or the base and the
 * target are the same. A bar at zero asserts that no progress has been made,
 * which is a different statement from having nothing to report, and only one
 * of them is true.
 */
export function progressOf(base: string, target: string, current: string): Progress | null {
  const from = readFigure(base)
  const to = readFigure(target)
  const now = readFigure(current)

  if (from === null || to === null || now === null) return null

  const distance = to - from
  // A target equal to its base is not a target. Dividing by it would give
  // infinity, and answering "already there" would be a claim nobody made.
  if (distance === 0) return null

  const travelled = (now - from) / distance

  return {
    fraction: Math.min(1, Math.max(0, travelled)),
    reached: travelled >= 1,
    regressing: travelled < 0,
  }
}
